import { Button, Input, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
import { type FormEvent, useState } from "react";
import { Async, PageHead, useAction } from "@/components/ui";
import { useT } from "@/i18n";
import { api, type EngineModel } from "@/lib/api";
import { useAsync } from "@/lib/hooks";

export function EnginesPage() {
	const t = useT();
	const engines = useAsync(() => api.engines(), []);

	return (
		<>
			<PageHead title={t("engines.title")} help={t("engines.lede")} />
			<Async state={engines}>{({ engines: list }) => <EngineForm engines={list} onSaved={engines.reload} />}</Async>
		</>
	);
}

function EngineForm({ engines, onSaved }: { engines: EngineModel[]; onSaved: () => void }) {
	const t = useT();
	const { run, busy } = useAction();
	const [models, setModels] = useState<Record<string, string>>(() =>
		Object.fromEntries(engines.map((e) => [e.engine, e.override ?? ""])),
	);

	async function submit(event: FormEvent) {
		event.preventDefault();
		const saved = await run(
			() => api.putEngines(models),
			() => t("state.saved"),
		);
		if (saved) onSaved();
	}

	return (
		<form className="form" onSubmit={submit}>
			<fieldset disabled={busy}>
				<div className="table-scroll">
					<Table className="text-[length:var(--t-sm)]">
						<TableHeader>
							<TableRow>
								<TableHead>{t("engines.engine")}</TableHead>
								<TableHead>{t("engines.market")}</TableHead>
								<TableHead>{t("engines.model")}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{engines.map((engine) => (
								<TableRow key={engine.engine}>
									<TableCell>
										{engine.label} <span className="mono faint">{engine.engine}</span>
									</TableCell>
									<TableCell>{t(`engines.market.${engine.market}`)}</TableCell>
									<TableCell>
										<Input
											aria-label={`${engine.label} ${t("engines.model")}`}
											value={models[engine.engine] ?? ""}
											placeholder={engine.default_model}
											onChange={(e) => setModels((prev) => ({ ...prev, [engine.engine]: e.target.value }))}
										/>
										{models[engine.engine]?.trim() ? (
											<span className="hint small">{t("engines.default", { model: engine.default_model })}</span>
										) : null}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
				<div className="row">
					<span className="spacer" />
					<Button type="submit">{t("state.save")}</Button>
				</div>
			</fieldset>
		</form>
	);
}
